import { useEffect } from 'react';

const SITE_URL = 'https://pricehuntify.com';
const DEFAULT_IMAGE = `${SITE_URL}/og-card.png`;

function upsertMeta(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function upsertCanonical(href) {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

export default function Seo({
  title,
  description,
  canonical,
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  schema,
}) {
  // Serialize for the dependency array: callers build `schema` inline, so a new
  // object identity arrives on every render and the effect would rewrite the
  // head DOM each time.
  const schemaJson = schema === undefined ? undefined : JSON.stringify(schema);

  useEffect(() => {
    const absoluteCanonical = canonical?.startsWith('http') ? canonical : `${SITE_URL}${canonical || '/'}`;
    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:url', absoluteCanonical);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:image', image);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);
    upsertCanonical(absoluteCanonical);

    // `undefined` means "this page has no opinion" — leave whatever the server
    // injected untouched. `null` explicitly clears it. Anything else replaces
    // it. The distinction matters: treating undefined as "clear" would delete
    // the server's structured data whenever a caller omits the prop.
    if (schemaJson === undefined) return;
    let script = document.getElementById('seo-structured-data');
    if (schemaJson !== 'null') {
      if (!script) {
        script = document.createElement('script');
        script.id = 'seo-structured-data';
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = schemaJson;
    } else if (script) {
      script.remove();
    }
  }, [title, description, canonical, image, type, noindex, schemaJson]);

  return null;
}
